import React, { useEffect, useState } from 'react';
import { dealMgmt } from '../../services/dealManagementApi';
import { Card, CardHead, Tabs, Badge, Modal } from '../../components/ui';
import { LoadingState, EmptyState, ErrorState, SaveIndicator } from '../../features/shared/PageStates';
import { humanize } from '../../lib/format';

const EMPTY_EMAIL = {
  name: '',
  template_key: '',
  service_type: 'all',
  subject: '',
  body: '',
};

const EMPTY_CONTRACT = {
  name: '',
  template_key: '',
  service_type: 'po_funding',
  body: '',
};

const EMAIL_VARS = '{{client_company_name}} · {{client_contact_person}} · {{deal_ref}} · {{supplier_name}} · {{end_client_name}}';
const CONTRACT_VARS = '{{client_company_name}} · {{deal_ref}} · {{capital_required}} · {{calculated_fee}} · {{expected_repayment_date}}';

export function TemplatesPage({ showToast }) {
  const [tab, setTab] = useState('Email');
  const [emailTpl, setEmailTpl] = useState([]);
  const [contractTpl, setContractTpl] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const [saveStatus, setSaveStatus] = useState('idle');
  const [editorOpen, setEditorOpen] = useState(false);
  const [editing, setEditing] = useState(null); // null = create
  const [form, setForm] = useState(EMPTY_EMAIL);

  const isEmail = tab === 'Email';
  const list = isEmail ? emailTpl : contractTpl;

  const load = () => {
    setLoading(true);
    setError('');
    Promise.all([
      dealMgmt.emailTemplates().catch(() => ({ templates: [] })),
      dealMgmt.contractTemplates().catch(() => ({ templates: [] })),
    ])
      .then(([e, c]) => {
        setEmailTpl(e.templates || e.items || []);
        setContractTpl(c.templates || c.items || []);
      })
      .catch(err => {
        setError(err.message);
        showToast?.(err.message, 'error');
      })
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setEditorOpen(false);
    setEditing(null);
    setForm(isEmail ? EMPTY_EMAIL : EMPTY_CONTRACT);
  }, [tab]); // eslint-disable-line react-hooks/exhaustive-deps

  const openCreate = () => {
    setEditing(null);
    setForm(isEmail ? { ...EMPTY_EMAIL } : { ...EMPTY_CONTRACT });
    setEditorOpen(true);
  };

  const openEdit = (tpl) => {
    setEditing(tpl);
    if (isEmail) {
      setForm({
        name: tpl.name || '',
        template_key: tpl.template_key || '',
        service_type: tpl.service_type || 'all',
        subject: tpl.subject || '',
        body: tpl.body || '',
      });
    } else {
      setForm({
        name: tpl.name || '',
        template_key: tpl.template_key || '',
        service_type: tpl.service_type || 'po_funding',
        body: tpl.body || tpl.content || '',
      });
    }
    setEditorOpen(true);
  };

  const set = (key, value) => setForm((p) => ({ ...p, [key]: value }));

  const save = async () => {
    if (busy) return;
    if (!form.name.trim()) {
      showToast?.('Name is required', 'error');
      return;
    }
    if (!form.body.trim()) {
      showToast?.('Body is required', 'error');
      return;
    }
    if (isEmail && !form.subject.trim()) {
      showToast?.('Subject is required', 'error');
      return;
    }

    setBusy(true);
    setSaveStatus('saving');
    try {
      if (isEmail) {
        const payload = {
          name: form.name.trim(),
          template_key: form.template_key.trim() || undefined,
          service_type: form.service_type,
          subject: form.subject.trim(),
          body: form.body,
        };
        if (editing) await dealMgmt.updateEmailTemplate(editing.id, payload);
        else await dealMgmt.createEmailTemplate(payload);
      } else {
        const payload = {
          name: form.name.trim(),
          template_key: form.template_key.trim() || undefined,
          service_type: form.service_type,
          body: form.body,
        };
        if (editing) await dealMgmt.updateContractTemplate(editing.id, payload);
        else await dealMgmt.createContractTemplate(payload);
      }
      setSaveStatus('saved');
      showToast?.(editing ? 'Template updated' : 'Template created');
      setEditorOpen(false);
      setEditing(null);
      load();
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e) {
      setSaveStatus('error');
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  const remove = async (tpl) => {
    if (busy) return;
    if (!window.confirm(`Delete template “${tpl.name || tpl.subject}”?`)) return;
    setBusy(true);
    setSaveStatus('saving');
    try {
      if (isEmail) await dealMgmt.deleteEmailTemplate(tpl.id);
      else await dealMgmt.deleteContractTemplate(tpl.id);
      setSaveStatus('saved');
      showToast?.('Template deleted');
      if (editing?.id === tpl.id) {
        setEditorOpen(false);
        setEditing(null);
      }
      load();
      setTimeout(() => setSaveStatus('idle'), 1500);
    } catch (e) {
      setSaveStatus('error');
      showToast?.(e.message, 'error');
    } finally {
      setBusy(false);
    }
  };

  if (loading) return <LoadingState label="Loading templates…" />;
  if (error) return <ErrorState message={error} onRetry={load} />;

  return (
    <div>
      <Card>
        <Tabs tabs={['Email', 'Contract']} active={tab} onChange={setTab} />
        <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '0 16px', flexWrap: 'wrap' }}>
          <CardHead
            title={`${tab} templates`}
            sub={`${list.length} template(s) · Merge fields e.g. {{client_company_name}}`}
          />
          <SaveIndicator status={saveStatus} />
          <div style={{ marginLeft: 'auto', display: 'flex', gap: 8, paddingBottom: 8 }}>
            <button type="button" className="btn btn-o btn-sm" onClick={load} disabled={busy}>Refresh</button>
            <button type="button" className="btn btn-p btn-sm" onClick={openCreate} disabled={busy}>
              + Add template
            </button>
          </div>
        </div>

        {list.length === 0 ? (
          <EmptyState
            title="No templates yet"
            sub="Create your first template to reuse on Emails and Contracting."
            action={openCreate}
            actionLabel="Add template"
          />
        ) : (
          <div style={{ padding: '0 16px 16px' }}>
            {list.map(t => (
              <div key={t.id} style={{ border: '1px solid var(--border)', borderRadius: 8, padding: 12, marginBottom: 10 }}>
                <div style={{ display: 'flex', gap: 8, alignItems: 'center', flexWrap: 'wrap' }}>
                  <strong style={{ fontSize: 13 }}>{t.name || t.subject || 'Untitled'}</strong>
                  {t.service_type && <Badge type="neutral">{humanize(t.service_type)}</Badge>}
                  {t.template_key && (
                    <span className="mono" style={{ fontSize: 11, color: 'var(--text3)' }}>{t.template_key}</span>
                  )}
                  <div style={{ marginLeft: 'auto', display: 'flex', gap: 6 }}>
                    <button type="button" className="btn btn-o btn-sm" disabled={busy} onClick={() => openEdit(t)}>
                      Edit
                    </button>
                    <button type="button" className="btn btn-o btn-sm" disabled={busy} onClick={() => remove(t)}>
                      Delete
                    </button>
                  </div>
                </div>
                {t.subject && (
                  <div style={{ fontSize: 12, color: 'var(--text2)', marginTop: 4 }}>
                    Subject: {t.subject}
                  </div>
                )}
                <pre style={{
                  marginTop: 8, fontSize: 11, background: 'var(--surface2)', padding: 10,
                  borderRadius: 6, whiteSpace: 'pre-wrap', maxHeight: 140, overflow: 'auto',
                }}>
                  {t.body || t.content || t.template_body || '—'}
                </pre>
              </div>
            ))}
          </div>
        )}
      </Card>

      <Modal
        open={editorOpen}
        onClose={busy ? undefined : () => { setEditorOpen(false); setEditing(null); }}
        title={editing ? `Edit ${isEmail ? 'email' : 'contract'} template` : `New ${isEmail ? 'email' : 'contract'} template`}
        width={720}
      >
        <div className="form-row fr2">
          <div>
            <label className="field-label">Name *</label>
            <input
              className="field-input"
              value={form.name}
              disabled={busy}
              onChange={(e) => set('name', e.target.value)}
              placeholder="e.g. Stage 1 document request"
              autoFocus
            />
          </div>
          <div>
            <label className="field-label">Key (optional)</label>
            <input
              className="field-input"
              value={form.template_key}
              disabled={busy}
              onChange={(e) => set('template_key', e.target.value)}
              placeholder="auto from name if blank"
            />
          </div>
        </div>

        <div className="form-row fr2" style={{ marginTop: 8 }}>
          <div>
            <label className="field-label">Service type</label>
            <select
              className="field-input"
              value={form.service_type}
              disabled={busy}
              onChange={(e) => set('service_type', e.target.value)}
            >
              {isEmail ? (
                <>
                  <option value="all">All</option>
                  <option value="po_funding">PO Funding</option>
                  <option value="invoice_discounting">Invoice Discounting</option>
                </>
              ) : (
                <>
                  <option value="po_funding">PO Funding</option>
                  <option value="invoice_discounting">Invoice Discounting</option>
                </>
              )}
            </select>
          </div>
          {isEmail && (
            <div>
              <label className="field-label">Subject *</label>
              <input
                className="field-input"
                value={form.subject}
                disabled={busy}
                onChange={(e) => set('subject', e.target.value)}
                placeholder="Document request — {{client_company_name}}"
              />
            </div>
          )}
        </div>

        <div style={{ marginTop: 8 }}>
          <label className="field-label">Body *</label>
          <textarea
            className="field-input"
            rows={12}
            value={form.body}
            disabled={busy}
            onChange={(e) => set('body', e.target.value)}
            placeholder="Dear {{client_contact_person}}, …"
            style={{ fontFamily: 'ui-monospace, SFMono-Regular, Menlo, monospace', fontSize: 12.5 }}
          />
          <div style={{ fontSize: 11, color: 'var(--text3)', marginTop: 6 }}>
            Merge fields: {isEmail ? EMAIL_VARS : CONTRACT_VARS}
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8, justifyContent: 'flex-end', marginTop: 16 }}>
          <button
            type="button"
            className="btn btn-o"
            disabled={busy}
            onClick={() => { setEditorOpen(false); setEditing(null); }}
          >
            Cancel
          </button>
          <button type="button" className="btn btn-p" disabled={busy} onClick={save}>
            {busy ? 'Saving…' : editing ? 'Save changes' : 'Create template'}
          </button>
        </div>
      </Modal>
    </div>
  );
}

export default TemplatesPage;
